import { useEffect, useMemo, useState } from 'react';
import { Dayjs } from 'dayjs';
import {
  Dialog,
  DialogActions,
  Button,
  Divider,
  DialogContent,
  Stack,
  MenuItem,
  Typography,
  Card,
} from '@mui/material';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useTheme } from '@mui/material/styles';

import { Strings } from '@app/constants';
import { LEAVE_CONFIG } from '@app/configs';
import { Header } from '@app/sections/header';
import { Iconify, RHFSelect, Scrollbar } from '@app/components';
import { ILeaveSchema, IPeriods } from '../types';
import { isWeekend } from '@app/utils';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  selectedDates: { from: Dayjs | null; to: Dayjs | null };
  totalLeavesCalculated: (totalLeaves: number) => void;
  handleSelectPeriod: (value: IPeriods[]) => void;
  methods: UseFormReturn<ILeaveSchema>;
}

const SelectPeriod = ({
  open,
  onClose,
  selectedDates,
  totalLeavesCalculated,
  handleSelectPeriod,
  methods,
}: Props) => {
  const [selectDate, setSelectDate] = useState<Dayjs[]>([]);

  const [selectedPeriods, setSelectedPeriods] = useState<Record<string, string>>({});

  const totalLeaves = useMemo(() => {
    return selectDate.reduce((count, date) => {
      const selectedValue = selectedPeriods[date.format('DD/MM/YYYY')];
      if (!selectedValue || selectedValue === 'Full Day') {
        return count + 1;
      } else if (selectedValue === '1st Half' || selectedValue === '2nd Half') {
        return count + 0.5;
      }
      return count;
    }, 0);
  }, [selectDate, selectedPeriods]);

  const theme = useTheme();

  const handleSelectedPeriod = (event: React.ChangeEvent<{ value: unknown }>, date: Dayjs) => {
    if (isWeekend(date)) {
      return;
    }

    const selectedValue = event.target.value as string;
    setSelectedPeriods((prevState) => ({
      ...prevState,
      [date.format('DD/MM/YYYY')]: selectedValue,
    }));

    const leaveToUpdate = periods.find((leave) => leave.date === date.format('DD/MM/YYYY'));
    if (leaveToUpdate) {
      leaveToUpdate.period = selectedValue;
    }
  };

  const periods = selectDate.map((dayjsObj) => {
    const formattedDate = dayjsObj.format('DD/MM/YYYY');
    const period = selectedPeriods[formattedDate] || Strings.leave.fullDay;
    return { date: formattedDate, period };
  });
  const handleSave = () => {
    const periods: IPeriods[] = selectDate.map((date) => ({
      date: date.format('DD/MM/YYYY'),
      period: selectedPeriods[date.format('DD/MM/YYYY')] || Strings.leave.fullDay,
    }));
    console.log('save', periods);
    handleSelectPeriod(periods);
    onClose();
  };

  const from = useWatch({ control: methods.control, name: 'from' });
  const to = useWatch({ control: methods.control, name: 'to' });

  useEffect(() => {
    handleSelectPeriod(periods);
    if (selectedDates.from && selectedDates.to) {
      const startDate = selectedDates.from;
      const endDate = selectedDates.to;
      const dates: Dayjs[] = [];
      let currentDate = startDate;
      while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
        if (LEAVE_CONFIG.SANDWICH_CONFIG || !isWeekend(currentDate)) {
          dates.push(currentDate);
        }
        currentDate = currentDate.add(1, 'day');
      }
      setSelectDate(dates);
    }
    totalLeavesCalculated(totalLeaves);
  }, [totalLeaves, selectedPeriods, from, to]);

  return (
    <>
      <Dialog open={open} fullWidth maxWidth={'xs'}>
        <Header variant="dialog" title={Strings.leave.selectPeriodForLeave} onClose={onClose} />
        <Divider />
        <Scrollbar>
          <DialogContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mt={1}>
              <Typography variant="body1" fontWeight={'bold'} pl={1}>
                {Strings.leave.date}
              </Typography>
              <Typography variant="body1" fontWeight={'bold'} mr={1.5}>
                {Strings.leave.selectPeriod}
              </Typography>
            </Stack>
            <Stack spacing={1} mt={1}>
              {selectDate.map((date, index) => (
                <Card key={index} sx={{ height: '60px', width: '100%', borderRadius: '5px' }}>
                  <Stack
                    key={index}
                    direction="row"
                    alignItems="center"
                    justifyContent={'space-between'}
                  >
                    <Typography p={1}>{date.format('DD/MM/YYYY')}</Typography>
                    <Typography p={1}>
                      {isWeekend(date) ? (
                        <Typography color={theme.palette.grey[500]} fontSize={14}>
                          Weekend
                        </Typography>
                      ) : (
                        <RHFSelect
                          required
                          name={'Full Day'}
                          defaultValue={Strings.leave.fullDay}
                          value={selectedPeriods[date.format('DD/MM/YYYY')]}
                          size="small"
                          sx={{ width: '105px' }}
                          onChange={(event) => {
                            handleSelectedPeriod(event, date);
                          }}
                        >
                          <MenuItem value={Strings.leave.fullDay} selected>
                            {Strings.leave.fullDay}
                          </MenuItem>
                          <MenuItem value={Strings.leave.firstHalf}>
                            {Strings.leave.firstHalf}
                          </MenuItem>
                          <MenuItem value={Strings.leave.secondHalf}>
                            {Strings.leave.secondHalf}
                          </MenuItem>
                        </RHFSelect>
                      )}
                    </Typography>
                  </Stack>
                </Card>
              ))}
            </Stack>
            <Stack direction={'row'} gap={1} pt={2} pb={2}>
              <Stack color={theme.palette.grey[600]}>
                <Iconify icon="mdi:progress-clock" />
              </Stack>
              <Stack direction={'row'} gap={0.5}>
                <Typography color={theme.palette.grey[500]} fontSize={14}>
                  {Strings.leave.youAreRequesting}
                </Typography>
                <Typography fontWeight={700} color={theme.palette.grey[600]} fontSize={15}>
                  {totalLeaves.toString()} {Strings.field.days}
                </Typography>
              </Stack>
            </Stack>
          </DialogContent>
        </Scrollbar>
        <Divider />
        <DialogActions>
          <Button variant="contained" color="primary" onClick={handleSave}>
            {Strings.button.save}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export { SelectPeriod };
